<script setup>
import { ref, computed, watch } from 'vue';
import { useStore } from 'vuex';
import crmAPI from '../api/crm';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  pipelines: {
    type: Array,
    default: () => [],
  },
  selectedPipelineId: {
    type: [Number, String],
    default: null,
  },
});

const emit = defineEmits(['close', 'pipeline-updated']);

const store = useStore();
const isSaving = ref(false);
const errorMessage = ref('');

const isTestingWebhook = ref(false);
const testWebhookResult = ref(null);
const showWebhookDetails = ref(false);

const agents = computed(() => store.getters['agents/getAgents'] || []);
const inboxes = computed(() => store.getters['inboxes/getInboxes'] || []);

const webhookEventsConfig = [
  {
    key: 'deal_created',
    title: 'Item Criado',
    description: 'Quando um novo item é criado no kanban',
  },
  {
    key: 'deal_updated',
    title: 'Item Atualizado',
    description: 'Quando um item existente é modificado',
  },
  {
    key: 'deal_deleted',
    title: 'Item Deletado',
    description: 'Quando um item é removido do kanban',
  },
  {
    key: 'deal_stage_changed',
    title: 'Mudança de Estágio',
    description: 'Quando um item muda de estágio/funil',
  },
  {
    key: 'deals_reordered',
    title: 'Itens Reordenados',
    description: 'Quando itens são reordenados dentro de um estágio',
  },
];

const formData = ref({
  id: null,
  name: '',
  description: '',
  inbox_id: null,
  inbox_ids: [],
  agent_ids: [],
  webhook_enabled: false,
  webhook_url: '',
  webhook_events: ['deal_created', 'deal_updated', 'deal_deleted', 'deal_stage_changed', 'deals_reordered'],
  stages: [],
});

const activeStages = computed(() => {
  return formData.value.stages.filter(s => !s._destroy);
});

const loadPipelineData = p => {
  testWebhookResult.value = null;
  showWebhookDetails.value = false;

  if (p) {
    formData.value = {
      id: p.id,
      name: p.name || '',
      description: p.description || '',
      inbox_id: p.inbox_id || null,
      inbox_ids: Array.isArray(p.inbox_ids) ? p.inbox_ids : [],
      agent_ids: Array.isArray(p.agent_ids) ? p.agent_ids : [],
      webhook_enabled: !!p.webhook_enabled,
      webhook_url: p.webhook_url || '',
      webhook_events: Array.isArray(p.webhook_events) && p.webhook_events.length > 0
        ? [...p.webhook_events]
        : ['deal_created', 'deal_updated', 'deal_deleted', 'deal_stage_changed', 'deals_reordered'],
      stages: (p.stages || []).map((s, idx) => ({
        id: s.id,
        name: s.name,
        color: s.color || '#3B82F6',
        stage_type: s.stage_type || 'open',
        position: s.position !== undefined ? s.position : idx,
        win_probability: s.win_probability || 100,
        deals_count: s.deals_count || 0,
        _destroy: false,
      })),
    };
  }
};

watch(
  () => props.show,
  newVal => {
    if (newVal) {
      const target = props.pipelines.find(p => Number(p.id) === Number(props.selectedPipelineId)) || props.pipelines[0];
      loadPipelineData(target);
      errorMessage.value = '';
    }
  }
);

const toggleAgentSelection = agentId => {
  const idNum = Number(agentId);
  const idx = formData.value.agent_ids.indexOf(idNum);
  if (idx > -1) {
    formData.value.agent_ids.splice(idx, 1);
  } else {
    formData.value.agent_ids.push(idNum);
  }
};

const handleTestWebhook = async () => {
  if (!formData.value.webhook_url || !formData.value.webhook_url.trim()) {
    alert('Por favor, digite ou cole a URL de destino do Webhook antes de testar.');
    return;
  }

  isTestingWebhook.value = true;
  testWebhookResult.value = null;

  try {
    const res = await crmAPI.testPipelineWebhook(formData.value.id, {
      webhook_url: formData.value.webhook_url.trim(),
      webhook_events: formData.value.webhook_events,
    });

    testWebhookResult.value = {
      success: true,
      message: res.data.message || 'Webhook entregue com sucesso (200 OK)!',
    };
  } catch (err) {
    const msg = err.response?.data?.message || err.message || 'Falha ao conectar com a URL do Webhook.';
    testWebhookResult.value = {
      success: false,
      message: msg,
    };
  } finally {
    isTestingWebhook.value = false;
  }
};

// Adiciona uma nova etapa de negociação antes de Ganho e Perdido
const addStage = () => {
  const nonDestroyed = formData.value.stages.filter(s => !s._destroy);
  const terminalIdx = nonDestroyed.findIndex(s => s.stage_type === 'won' || s.stage_type === 'lost');
  
  let targetIndex = formData.value.stages.length;
  if (terminalIdx >= 0) {
    const targetStage = nonDestroyed[terminalIdx];
    targetIndex = formData.value.stages.indexOf(targetStage);
  }

  formData.value.stages.splice(targetIndex, 0, {
    name: 'Nova Etapa',
    color: '#3B82F6',
    stage_type: 'open',
    position: targetIndex,
    win_probability: 50,
    deals_count: 0,
    _destroy: false,
  });

  formData.value.stages.forEach((s, idx) => {
    s.position = idx;
  });
};

const removeStage = stage => {
  if (stage.stage_type === 'won' || stage.stage_type === 'lost') {
    return;
  }

  if (stage.deals_count > 0) {
    const confirmDelete = confirm(
      `Atenção: Existem ${stage.deals_count} leads nesta etapa. Ao excluí-la, esses leads serão removidos do funil. Deseja continuar?`
    );
    if (!confirmDelete) return;
  }

  if (stage.id) {
    stage._destroy = true;
  } else {
    const idx = formData.value.stages.indexOf(stage);
    if (idx > -1) {
      formData.value.stages.splice(idx, 1);
    }
  }
};

const isFirstOpenStage = stage => {
  const openStages = formData.value.stages.filter(s => !s._destroy && s.stage_type === 'open');
  return openStages[0] === stage;
};

const isLastOpenStage = stage => {
  const openStages = formData.value.stages.filter(s => !s._destroy && s.stage_type === 'open');
  return openStages[openStages.length - 1] === stage;
};

const moveStageUp = stage => {
  const currentIdx = formData.value.stages.indexOf(stage);
  if (currentIdx <= 0) return;

  // Localiza a etapa anterior visível
  let prevIdx = currentIdx - 1;
  while (prevIdx >= 0 && formData.value.stages[prevIdx]._destroy) {
    prevIdx--;
  }
  if (prevIdx < 0) return;

  const prevStage = formData.value.stages[prevIdx];
  if (prevStage.stage_type !== 'open') return;

  formData.value.stages[currentIdx] = prevStage;
  formData.value.stages[prevIdx] = stage;

  formData.value.stages.forEach((s, idx) => {
    s.position = idx;
  });
};

const moveStageDown = stage => {
  const currentIdx = formData.value.stages.indexOf(stage);
  if (currentIdx >= formData.value.stages.length - 1) return;

  // Localiza a próxima etapa visível
  let nextIdx = currentIdx + 1;
  while (nextIdx < formData.value.stages.length && formData.value.stages[nextIdx]._destroy) {
    nextIdx++;
  }
  if (nextIdx >= formData.value.stages.length) return;

  const nextStage = formData.value.stages[nextIdx];
  if (nextStage.stage_type !== 'open') return;

  formData.value.stages[currentIdx] = nextStage;
  formData.value.stages[nextIdx] = stage;

  formData.value.stages.forEach((s, idx) => {
    s.position = idx;
  });
};

const handleSave = async () => {
  if (!formData.value.name.trim()) {
    errorMessage.value = 'O nome do funil é obrigatório.';
    return;
  }

  const validStages = formData.value.stages.filter(s => !s._destroy);
  if (validStages.length === 0) {
    errorMessage.value = 'O funil deve conter pelo menos uma etapa.';
    return;
  }

  isSaving.value = true;
  errorMessage.value = '';

  const payload = {
    name: formData.value.name,
    description: formData.value.description,
    inbox_id: formData.value.inbox_id,
    agent_ids: formData.value.agent_ids,
    webhook_enabled: formData.value.webhook_enabled,
    webhook_url: formData.value.webhook_url,
    webhook_events: formData.value.webhook_events,
    stages_attributes: formData.value.stages.map((s, idx) => ({
      id: s.id || undefined,
      name: s.name,
      color: s.color,
      stage_type: s.stage_type,
      position: idx,
      win_probability: s.win_probability,
      _destroy: s._destroy ? '1' : undefined,
    })),
  };

  try {
    if (formData.value.id) {
      const res = await crmAPI.updatePipeline(formData.value.id, payload);
      emit('pipeline-updated', res.data);
    }
    emit('close');
  } catch (err) {
    console.error('Error saving pipeline:', err);
    errorMessage.value = err.response?.data?.errors?.join(', ') || 'Erro ao salvar as configurações.';
  } finally {
    isSaving.value = false;
  }
};
</script>

<template>
  <div
    v-if="show"
    class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 dark:bg-slate-950/80 backdrop-blur-xs transition-opacity animate-fade-in"
    @click.self="emit('close')"
  >
    <div
      class="w-full max-w-3xl max-h-[92vh] flex flex-col bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-2xl overflow-hidden text-slate-800 dark:text-slate-100"
    >
      <!-- Header -->
      <div class="px-6 py-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between bg-slate-50 dark:bg-slate-950/60">
        <div class="flex items-center gap-3">
          <div class="w-8 h-8 rounded-xl bg-blue-600/10 dark:bg-blue-600/20 border border-blue-500/30 flex items-center justify-center text-blue-600 dark:text-blue-400">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </div>
          <div>
            <h3 class="text-sm font-bold text-slate-900 dark:text-white">Configurações do Funil de Vendas</h3>
            <p class="text-xs text-slate-500 dark:text-slate-400">Personalize os nomes das colunas, cores, agentes responsáveis e webhooks.</p>
          </div>
        </div>

        <button
          type="button"
          class="p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-white rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors cursor-pointer"
          @click="emit('close')"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <!-- Error Alert -->
      <div v-if="errorMessage" class="mx-6 mt-4 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-500/50 rounded-xl text-xs text-red-600 dark:text-red-300">
        {{ errorMessage }}
      </div>

      <!-- Form Details Panel -->
      <div class="flex-1 p-6 overflow-y-auto space-y-6">
        <!-- Basic Info -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label class="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
              Nome do Funil <span class="text-red-500 dark:text-red-400">*</span>
            </label>
            <input
              v-model="formData.name"
              type="text"
              placeholder="Ex: Funil Principal de Vendas"
              class="w-full h-9 px-3 text-xs bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700/80 rounded-xl focus:ring-2 focus:ring-blue-500 focus:outline-none text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500"
            />
          </div>

          <div>
            <label class="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
              Caixa de Entrada Padrão
            </label>
            <select
              v-model="formData.inbox_id"
              class="w-full h-9 px-3 text-xs bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700/80 rounded-xl focus:ring-2 focus:ring-blue-500 focus:outline-none text-slate-900 dark:text-white"
            >
              <option :value="null">Qualquer Caixa de Entrada (Padrão)</option>
              <option v-for="inbox in inboxes" :key="inbox.id" :value="inbox.id">
                {{ inbox.name }}
              </option>
            </select>
          </div>
        </div>

        <div>
          <label class="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
            Descrição do Funil
          </label>
          <input
            v-model="formData.description"
            type="text"
            placeholder="Ex: Funil unificado de leads e atendimento de vendas via WhatsApp."
            class="w-full h-9 px-3 text-xs bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700/80 rounded-xl focus:ring-2 focus:ring-blue-500 focus:outline-none text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500"
          />
        </div>

        <!-- Stages Configuration (Destaque Principal) -->
        <div class="pt-4 border-t border-slate-200 dark:border-slate-800">
          <div class="flex items-center justify-between mb-2.5">
            <div>
              <h4 class="text-xs font-bold text-slate-900 dark:text-slate-200">
                Etapas do Funil & Cores das Colunas
              </h4>
              <p class="text-[11px] text-slate-500 dark:text-slate-400">
                Edite os nomes das colunas, altere as cores e adicione etapas intermediárias de negociação.
              </p>
            </div>
            <button
              type="button"
              class="px-2.5 py-1 text-xs font-semibold text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 bg-blue-50 dark:bg-blue-500/10 hover:bg-blue-100 dark:hover:bg-blue-500/20 border border-blue-200 dark:border-blue-500/30 rounded-lg transition-colors flex items-center gap-1 cursor-pointer"
              @click="addStage"
            >
              + Adicionar Etapa
            </button>
          </div>

          <div class="space-y-2">
            <div
              v-for="stage in activeStages"
              :key="stage.id || stage.position"
              class="flex items-center gap-2.5 p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800/90 border border-slate-200 dark:border-slate-700/80 transition-all hover:border-slate-300 dark:hover:border-slate-600"
            >
              <!-- Color block preview / picker -->
              <div class="relative shrink-0 flex items-center justify-center">
                <input
                  v-model="stage.color"
                  type="color"
                  class="w-7 h-7 rounded-lg cursor-pointer bg-transparent border-0 p-0"
                  title="Alterar cor da coluna"
                />
              </div>

              <!-- Stage Name Input (Amplo e 100% visível) -->
              <div class="flex-1 min-w-0">
                <input
                  v-model="stage.name"
                  type="text"
                  placeholder="Nome da Etapa (ex: Novo Lead, Proposta, Negociação...)"
                  class="w-full h-8 px-3 text-xs font-semibold bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700/80 rounded-lg text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <!-- Status Tag (Ganho / Perdido / Negociação) -->
              <div class="shrink-0">
                <span
                  v-if="stage.stage_type === 'won'"
                  class="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-bold bg-emerald-50 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-500/30 select-none"
                >
                  <span>🏆</span>
                  <span>Ganho (Fechamento)</span>
                </span>
                <span
                  v-else-if="stage.stage_type === 'lost'"
                  class="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-bold bg-red-50 dark:bg-red-500/15 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-500/30 select-none"
                >
                  <span>❌</span>
                  <span>Perdido</span>
                </span>
                <span
                  v-else
                  class="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-medium bg-slate-200/70 dark:bg-slate-900 text-slate-700 dark:text-slate-400 border border-slate-300 dark:border-slate-700/60 select-none"
                >
                  <span>Em aberto</span>
                </span>
              </div>

              <!-- Deals count in this stage badge -->
              <span
                class="text-[11px] px-2.5 py-1 rounded-lg bg-slate-200/70 dark:bg-slate-900 text-slate-700 dark:text-slate-300 font-semibold shrink-0 border border-slate-300 dark:border-slate-700/60 select-none"
                :title="`${stage.deals_count || 0} leads nesta etapa`"
              >
                {{ stage.deals_count || 0 }} leads
              </span>

              <!-- Reorder buttons (Apenas para etapas de negociação intermediárias) -->
              <div class="flex items-center gap-0.5 shrink-0">
                <button
                  v-if="stage.stage_type === 'open'"
                  type="button"
                  class="p-1 rounded-md text-slate-400 hover:text-slate-700 dark:hover:text-white hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors disabled:opacity-30 cursor-pointer"
                  :disabled="isFirstOpenStage(stage)"
                  title="Mover para cima"
                  @click="moveStageUp(stage)"
                >
                  <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 15l7-7 7 7" />
                  </svg>
                </button>

                <button
                  v-if="stage.stage_type === 'open'"
                  type="button"
                  class="p-1 rounded-md text-slate-400 hover:text-slate-700 dark:hover:text-white hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors disabled:opacity-30 cursor-pointer"
                  :disabled="isLastOpenStage(stage)"
                  title="Mover para baixo"
                  @click="moveStageDown(stage)"
                >
                  <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              </div>

              <!-- Delete Stage Button / Lock Indicator -->
              <div class="w-6 shrink-0 flex items-center justify-center">
                <button
                  v-if="stage.stage_type === 'open'"
                  type="button"
                  class="p-1 rounded-md text-red-500 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-100 dark:hover:bg-red-950/40 transition-colors cursor-pointer"
                  title="Remover etapa"
                  @click="removeStage(stage)"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
                <span
                  v-else
                  class="text-slate-400 dark:text-slate-500 cursor-not-allowed text-xs"
                  title="Etapa final fixa (obrigatória para o fechamento do CRM)"
                >
                  🔒
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Responsible Agents Selection -->
        <div class="pt-4 border-t border-slate-200 dark:border-slate-800">
          <label class="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1.5">
            Agentes / Vendedores com Acesso ao Funil
          </label>
          <div class="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-32 overflow-y-auto p-2.5 bg-slate-50 dark:bg-slate-950/60 border border-slate-200 dark:border-slate-800 rounded-xl">
            <label
              v-for="agent in agents"
              :key="agent.id"
              class="flex items-center gap-2 p-1.5 rounded-lg hover:bg-slate-200/60 dark:hover:bg-slate-800/80 cursor-pointer text-xs text-slate-700 dark:text-slate-300 select-none"
            >
              <input
                type="checkbox"
                :checked="formData.agent_ids.includes(Number(agent.id))"
                class="rounded bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 text-blue-600 focus:ring-blue-500 cursor-pointer"
                @change="toggleAgentSelection(agent.id)"
              />
              <span class="truncate">{{ agent.name }}</span>
            </label>
            <div v-if="agents.length === 0" class="text-xs text-slate-400 dark:text-slate-500 col-span-3">
              Nenhum agente cadastrado.
            </div>
          </div>
        </div>

        <!-- Webhooks Section -->
        <div class="pt-4 border-t border-slate-200 dark:border-slate-800 space-y-3">
          <div
            class="p-4 rounded-xl transition-all duration-200 flex items-center justify-between border"
            :class="
              formData.webhook_enabled
                ? 'bg-blue-50/80 dark:bg-[#0f1f3d] border-blue-300 dark:border-blue-500 shadow-sm dark:shadow-[0_0_15px_rgba(59,130,246,0.25)]'
                : 'bg-slate-50 dark:bg-[#0b1329] border-slate-200 dark:border-slate-800'
            "
          >
            <div class="flex items-center gap-3">
              <div
                class="w-9 h-9 rounded-xl flex items-center justify-center transition-colors shrink-0"
                :class="
                  formData.webhook_enabled
                    ? 'bg-blue-600 text-white'
                    : 'bg-slate-200 dark:bg-slate-800 text-slate-600 dark:text-slate-400'
                "
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <div>
                <div class="flex items-center gap-2">
                  <h4
                    class="text-xs font-bold"
                    :class="formData.webhook_enabled ? 'text-blue-900 dark:text-white' : 'text-slate-800 dark:text-slate-200'"
                  >
                    Integração por Webhook em Tempo Real
                  </h4>
                  <span
                    v-if="formData.webhook_enabled"
                    class="px-2 py-0.5 text-[10px] font-bold rounded-full bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-500/30"
                  >
                    Ativo
                  </span>
                </div>
                <p
                  class="text-[11px]"
                  :class="formData.webhook_enabled ? 'text-blue-700 dark:text-blue-300' : 'text-slate-500 dark:text-slate-400'"
                >
                  Envie automaticamente os eventos do Kanban para Make, n8n, Typebot ou seu CRM externo.
                </p>
              </div>
            </div>

            <label class="relative inline-flex items-center cursor-pointer select-none">
              <input
                v-model="formData.webhook_enabled"
                type="checkbox"
                class="sr-only peer"
              />
              <div class="w-11 h-6 bg-slate-300 dark:bg-slate-800 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600 border border-slate-300 dark:border-slate-700"></div>
            </label>
          </div>

          <div v-if="formData.webhook_enabled" class="space-y-4 pl-1 pr-1 animate-fade-in">
            <div>
              <label class="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
                URL de Destino do Webhook (Endpoint HTTP POST) <span class="text-red-500 dark:text-red-400">*</span>
              </label>
              <div class="flex gap-2">
                <input
                  v-model="formData.webhook_url"
                  type="url"
                  placeholder="https://seu-servidor.com/webhook-crm ou webhook.n8n.cloud/..."
                  class="flex-1 h-9 px-3 text-xs bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700/80 rounded-xl focus:ring-2 focus:ring-blue-500 focus:outline-none text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 font-mono"
                />
                <button
                  type="button"
                  class="px-3 h-9 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 text-xs font-semibold rounded-xl border border-slate-300 dark:border-slate-700/80 transition-colors flex items-center gap-1.5 disabled:opacity-50 cursor-pointer shrink-0"
                  :disabled="isTestingWebhook || !formData.webhook_url"
                  @click="handleTestWebhook"
                >
                  <div
                    v-if="isTestingWebhook"
                    class="w-3 h-3 border-2 border-slate-500 dark:border-slate-300 border-t-transparent rounded-full animate-spin"
                  ></div>
                  <svg v-else class="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                  <span>Testar URL</span>
                </button>
              </div>
            </div>

            <!-- Test Result Alert Banner -->
            <div
              v-if="testWebhookResult"
              class="p-3 rounded-xl text-xs flex items-center gap-2 border animate-fade-in"
              :class="
                testWebhookResult.success
                  ? 'bg-emerald-50 dark:bg-emerald-950/40 border-emerald-300 dark:border-emerald-600 text-emerald-800 dark:text-emerald-300'
                  : 'bg-red-50 dark:bg-red-950/40 border-red-300 dark:border-red-600 text-red-800 dark:text-red-300'
              "
            >
              <svg v-if="testWebhookResult.success" class="w-4 h-4 text-emerald-600 dark:text-emerald-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
              </svg>
              <svg v-else class="w-4 h-4 text-red-600 dark:text-red-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
              <span class="font-medium">{{ testWebhookResult.message }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer Buttons -->
      <div class="px-6 py-3.5 border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 flex items-center justify-end gap-2.5">
        <button
          type="button"
          class="px-4 py-2 text-xs font-medium text-slate-700 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white bg-slate-200/70 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 rounded-xl transition-colors cursor-pointer"
          @click="emit('close')"
        >
          Cancelar
        </button>

        <button
          type="button"
          class="px-5 py-2 text-xs font-bold text-white bg-blue-600 hover:bg-blue-500 active:bg-blue-700 rounded-xl shadow-md transition-all flex items-center gap-1.5 disabled:opacity-50 cursor-pointer"
          :disabled="isSaving"
          @click="handleSave"
        >
          <div
            v-if="isSaving"
            class="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"
          ></div>
          <span>Salvar Configurações</span>
        </button>
      </div>
    </div>
  </div>
</template>
